import { useCallback, useMemo } from "react";
import { useQueryCore, invalidateQuery } from "@/hooks/useQueryCore";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { getPrayers, answerPrayer, revertPrayer } from "@/lib/api";

const sortPrayers = (data) =>
  (data || []).slice().sort((a, b) => {
    const ta = a.date || a.created_at || "";
    const tb = b.date || b.created_at || "";
    return tb.localeCompare(ta);
  });

/**
 * Loads all prayers for the district once; filters client-side per tab.
 * Eliminates per-tab network round-trips and enables instant tab switching.
 */
export function usePrayers({ filter = "activa" } = {}) {
  const { districtId, coordinatorChurchId } = useDistrictGuard();

  const { data: allPrayers, loading, error, refetch, setData } = useQueryCore({
    key: ["prayers", districtId, coordinatorChurchId],
    enabled: !!districtId,
    queryFn: () => getPrayers({
      district_id: districtId,
    }),
    sortFn: sortPrayers,
  });

  const prayers = useMemo(() => {
    if (!allPrayers) return [];
    if (filter === "todas") return allPrayers;
    return allPrayers.filter((p) => p.status === filter);
  }, [allPrayers, filter]);

  const answer = useCallback(async (id) => {
    if (!districtId) return;
    setData((prev) =>
      (prev || []).map((p) => p.id === id ? { ...p, status: "respondida" } : p)
    );
    try {
      await answerPrayer(id);
      invalidateQuery(["prayers", districtId]);
    } catch {
      await refetch();
      throw new Error("Error al actualizar");
    }
  }, [districtId, setData, refetch]);

  const revert = useCallback(async (id) => {
    if (!districtId) return;
    setData((prev) =>
      (prev || []).map((p) => p.id === id ? { ...p, status: "activa" } : p)
    );
    try {
      await revertPrayer(id);
      invalidateQuery(["prayers", districtId]);
    } catch {
      await refetch();
      throw new Error("Error al actualizar");
    }
  }, [districtId, setData, refetch]);

  return {
    prayers,
    allPrayers,
    loading,
    error,
    refetch,
    answer,
    revert,
  };
}
